import { createAsyncAction } from 'react-redux-await-control';
import { message } from 'antd';
import { ticket } from '~/services';
import { handleErrors } from '~/common/utils/errorHandler';

export const getAllResolvingAreas = createAsyncAction('GET_ALL_RESOLVING_AREAS', {
  initialValue: [],
  saveResult: true,
  context: {
    request: () => ticket.get('/resolving-areas'),
    responseParser: ({ data }) => data.areas || [],
    errorParser: () => [],
    onRequestError: (err) => handleErrors(err, { defaultMessage: 'Erro ao buscar áreas' }),
  },
});

export const getDistributionAreas = createAsyncAction('GET_DISTRIBUTION_AREAS', {
  initialValue: [],
  saveResult: true,
  context: {
    request: () => ticket.get('/distribution/config'),
    responseParser: ({ data }) => data,
    errorParser: () => [],
    onRequestError: (err) => handleErrors(err, { defaultMessage: 'Erro ao buscar áreas resolvedoras' }),
  },
});

export const createDistributionArea = createAsyncAction('CREATE_DISTRIBUTION_AREA', {
  context: {
    request: (body) => ticket.post('/distribution/config', body),
    onRequestError: (err) => {
      const devMsg = err?.response?.data?.developerMessage || '';
      if (devMsg.includes('duplicate key')) {
        message.error('Essa área resolvedora já possui configuração cadastrada.');
        return;
      }
      handleErrors(err, { defaultMessage: 'Erro ao criar área' });
    },
    onRequestSuccess: () => message.success('Área criada com sucesso'),
  },
});

export const updateDistributionArea = createAsyncAction('UPDATE_DISTRIBUTION_AREA', {
  context: {
    request: ({ id, ...body }) => ticket.put(`/distribution/config/${id}`, body),
    onRequestError: (err) => handleErrors(err, { defaultMessage: 'Erro ao atualizar área' }),
    onRequestSuccess: () => message.success('Área atualizada com sucesso'),
  },
});

export const deleteDistributionArea = createAsyncAction('DELETE_DISTRIBUTION_AREA', {
  context: {
    request: ({ id, loggedUser }: { id: number; loggedUser: string }) =>
      ticket.delete(`/distribution/config/${id}`, { params: { loggedUser } }),
    onRequestError: (err) => handleErrors(err, { defaultMessage: 'Erro ao remover área' }),
    onRequestSuccess: () => message.success('Área removida com sucesso'),
  },
});

export const getDistributionTeams = createAsyncAction('GET_DISTRIBUTION_TEAMS', {
  initialValue: [],
  saveResult: true,
  context: {
    request: () => ticket.get('/teams'),
    responseParser: ({ data }) => data.teams || [],
    errorParser: () => [],
    onRequestError: (err) => handleErrors(err, { defaultMessage: 'Erro ao buscar times' }),
  },
});

export const getDistributionAgents = createAsyncAction('GET_DISTRIBUTION_AGENTS', {
  initialValue: { content: [], totalElements: 0 },
  saveResult: true,
  context: {
    request: (params) => ticket.get('/users/distribution-agents', { params }),
    responseParser: ({ data }) => ({ content: data.content, totalElements: data.totalElements }),
    errorParser: () => ({ content: [], totalElements: 0 }),
    onRequestError: (err) => handleErrors(err, { defaultMessage: 'Erro ao buscar agentes' }),
  },
});

export const getDistributionFilterOptions = createAsyncAction('GET_DISTRIBUTION_FILTER_OPTIONS', {
  initialValue: { areas: [], teams: [] },
  saveResult: true,
  context: {
    request: () => ticket.get('/users/distribution-agents', { params: { page: 0, size: 9999 } }),
    responseParser: ({ data }) => {
      const agents: any[] = data.content || [];
      const areaMap = new Map<number, { value: number; label: string }>();
      const teamMap = new Map<number, { value: number; label: string }>();
      agents.forEach((a) => {
        if (a.resolvingAreaId && a.resolvingAreaName) {
          areaMap.set(a.resolvingAreaId, { value: a.resolvingAreaId, label: a.resolvingAreaName });
        }
        if (a.teamId && a.teamName) {
          teamMap.set(a.teamId, { value: a.teamId, label: a.teamName });
        }
      });
      return {
        areas: Array.from(areaMap.values()),
        teams: Array.from(teamMap.values()),
      };
    },
    errorParser: () => ({ areas: [], teams: [] }),
    onRequestError: (err) => handleErrors(err, { defaultMessage: 'Erro ao buscar filtros' }),
  },
});

export const getAllDistributionAgentEmails = createAsyncAction('GET_ALL_DISTRIBUTION_AGENT_EMAILS', {
  initialValue: [],
  saveResult: true,
  context: {
    request: () => ticket.get('/users/distribution-agents', { params: { page: 0, size: 9999 } }),
    responseParser: ({ data }) => (data.content || []).map((a: any) => a.email).filter(Boolean),
    errorParser: () => [],
    onRequestError: (err) => handleErrors(err, { defaultMessage: 'Erro ao buscar agentes' }),
  },
});

export const searchTelliResponsibles = createAsyncAction('SEARCH_TELLI_RESPONSIBLES', {
  initialValue: [],
  saveResult: true,
  context: {
    request: (responsibleName) => ticket.get('/responsibles', { params: { responsibleName, size: 20, page: 0 } }),
    responseParser: ({ data }) => {
      if (data?._embedded) {
        const key = Object.keys(data._embedded)[0];
        return key ? data._embedded[key] || [] : [];
      }
      return Array.isArray(data) ? data : [];
    },
    errorParser: () => [],
    onRequestError: (err) => handleErrors(err, { defaultMessage: 'Erro ao buscar responsáveis' }),
  },
});

export const createDistributionAgent = createAsyncAction('CREATE_DISTRIBUTION_AGENT', {
  saveResult: true,
  context: {
    request: (body) => ticket.post('/users', body),
    responseParser: ({ data }) => data,
    onRequestError: (err) => {
      const devMsg = err?.response?.data?.developerMessage || '';
      if (devMsg.includes('users_email_unique') || devMsg.includes('duplicate key')) {
        message.error('Este agente já está cadastrado na distribuição.');
        return;
      }
      handleErrors(err, { defaultMessage: 'Erro ao cadastrar agente' });
    },
    onRequestSuccess: () => message.success('Agente cadastrado com sucesso'),
  },
});

export const updateAgent = createAsyncAction('UPDATE_AGENT', {
  context: {
    request: ({ id, ...body }) => ticket.put(`/users/${id}/update`, body),
    onRequestError: (err) => handleErrors(err, { defaultMessage: 'Erro ao atualizar agente' }),
  },
});

export const updateAgentFlag = createAsyncAction('UPDATE_AGENT_FLAG', {
  context: {
    request: ({ id, optionId, loggedUser }: { id: number; optionId: number; loggedUser: string }) =>
      ticket.patch(`/users/${id}/distribution-flag`, { optionId, loggedUser }),
    onRequestError: (err) => handleErrors(err, { defaultMessage: 'Erro ao atualizar flag do agente' }),
  },
});

export const getDistributionFlagOptions = createAsyncAction('GET_DISTRIBUTION_FLAG_OPTIONS', {
  initialValue: [],
  saveResult: true,
  context: {
    request: () => ticket.get('/distribution-flag-options'),
    responseParser: ({ data }) => data,
    onRequestError: (err) => handleErrors(err, { defaultMessage: 'Erro ao buscar opções de flag' }),
  },
});

export const findAgentByEmail = createAsyncAction('FIND_AGENT_BY_EMAIL', {
  saveResult: true,
  context: {
    request: (email: string) => ticket.get('/users/search', { params: { email } }),
    responseParser: ({ data }) => data,
    onRequestError: (err) => {
      if (err?.response?.status === 404) return;
      handleErrors(err, { defaultMessage: 'Erro ao buscar agente' });
    },
  },
});

export const enableDistributionAgent = createAsyncAction('ENABLE_DISTRIBUTION_AGENT', {
  context: {
    request: ({ id }: { id: number }) => ticket.patch(`/users/${id}/enable`),
    onRequestError: (err) => handleErrors(err, { defaultMessage: 'Erro ao reativar agente' }),
  },
});

export const removeDistributionAgent = createAsyncAction('REMOVE_DISTRIBUTION_AGENT', {
  context: {
    // loggedUser é enviado para que o backend possa registrar o log de auditoria da remoção
    request: ({ id, loggedUser }: { id: number; loggedUser: string }) =>
      ticket.patch(`/users/${id}/disable`, { loggedUser }),
    onRequestError: (err) => handleErrors(err, { defaultMessage: 'Erro ao remover agente' }),
    onRequestSuccess: () => message.success('Agente removido com sucesso'),
  },
});

export const getLinkedAgents = createAsyncAction('GET_LINKED_AGENTS', {
  initialValue: { count: 0, agents: [] },
  saveResult: true,
  context: {
    request: ({ areaId }: { areaId: number }) => ticket.get(`/distribution/config/${areaId}/linked-agents`),
    responseParser: ({ data }) => data,
    errorParser: () => ({ count: 0, agents: [] }),
    onRequestError: (err) => handleErrors(err, { defaultMessage: 'Erro ao buscar agentes vinculados' }),
  },
});

export const getAuditLogs = createAsyncAction('GET_AUDIT_LOGS', {
  initialValue: { content: [], totalElements: 0 },
  saveResult: true,
  context: {
    request: (params) => ticket.get('/distribution/audit-logs', { params }),
    responseParser: ({ data }) => ({ content: data.content, totalElements: data.totalElements }),
    errorParser: () => ({ content: [], totalElements: 0 }),
    onRequestError: (err) => handleErrors(err, { defaultMessage: 'Erro ao buscar logs de auditoria' }),
  },
});

export const getAuditLogFilters = createAsyncAction('GET_AUDIT_LOG_FILTERS', {
  initialValue: { modules: [], actions: [], authors: [] },
  saveResult: true,
  context: {
    request: () => ticket.get('/distribution/audit-logs/filters'),
    responseParser: ({ data }) => data,
    errorParser: () => ({ modules: [], actions: [], authors: [] }),
    onRequestError: (err) => handleErrors(err, { defaultMessage: 'Erro ao buscar filtros' }),
  },
});
